/**
 * RichEditor.js
 * 
 * Full-featured rich text editor using Quill.js + KaTeX loaded via CDN.
 * Zero npm installs required — loads scripts dynamically on first use.
 * 
 * Features:
 *  - Bold, Italic, Underline, Strike
 *  - Headers (H1, H2), blockquote, code block
 *  - Ordered + unordered lists
 *  - Math formulas via KaTeX (inline and block)
 *  - Code snippets with syntax highlighting
 *  - Image upload (base64 embedded or URL)
 *  - Superscript / Subscript
 *  - Text colour + background colour
 *  - Clean HTML output stored in DB
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';

// ── Load script helper ────────────────────────────────────────────────────────
function loadScript(src, id) {
  return new Promise((res, rej) => {
    if (document.getElementById(id)) { res(); return; }
    const s = document.createElement('script');
    s.src = src; s.id = id; s.async = true;
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}
function loadLink(href, id) {
  if (document.getElementById(id)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet'; l.href = href; l.id = id;
  document.head.appendChild(l);
}

// ── Quill CDN URLs ────────────────────────────────────────────────────────────
const QUILL_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css';
const QUILL_JS  = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.min.js';
const KATEX_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.8/katex.min.css';
const KATEX_JS  = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.8/katex.min.js';

let quillLoaded = false;
let quillLoading = null;

const loadQuill = () => {
  if (quillLoaded) return Promise.resolve();
  if (quillLoading)  return quillLoading;
  loadLink(QUILL_CSS,  'quill-css');
  loadLink(KATEX_CSS,  'katex-css');
  quillLoading = loadScript(KATEX_JS, 'katex-js')
    .then(() => loadScript(QUILL_JS, 'quill-js'))
    .then(() => { quillLoaded = true; quillLoading = null; });
  return quillLoading;
};

// ── Quill toolbar config ──────────────────────────────────────────────────────
const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'formula'],
  ['clean'],
];

// ── Math input modal ──────────────────────────────────────────────────────────
function MathModal({ onInsert, onClose }) {
  const [latex,   setLatex]   = useState('');
  const [preview, setPreview] = useState('');
  const [isBlock, setIsBlock] = useState(false);

  const renderPreview = useCallback((tex) => {
    if (!window.katex || !tex.trim()) { setPreview(''); return; }
    try {
      const html = window.katex.renderToString(tex, { throwOnError: false, displayMode: isBlock });
      setPreview(html);
    } catch {
      setPreview('<span style="color:red">Invalid LaTeX</span>');
    }
  }, [isBlock]);

  useEffect(() => { renderPreview(latex); }, [latex, renderPreview]);

  const examples = [
    { label:'Fraction',    tex:'\\frac{a}{b}'       },
    { label:'Square root', tex:'\\sqrt{x}'           },
    { label:'Power',       tex:'x^{2} + y^{2}'      },
    { label:'Integral',    tex:'\\int_0^\\infty e^{-x} dx' },
    { label:'Sum',         tex:'\\sum_{i=1}^{n} i'  },
    { label:'Matrix',      tex:'\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
    { label:'Limit',       tex:'\\lim_{x \\to \\infty} f(x)' },
    { label:'Greek',       tex:'\\alpha + \\beta = \\gamma'  },
  ];

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.7)',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div style={{
        background:'var(--bg-card)', borderRadius:14, border:'1px solid var(--border)',
        padding:28, maxWidth:540, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>∑ Insert Math / Formula</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>

        {/* Display / inline toggle */}
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {[false, true].map(block => (
            <button key={block} onClick={() => setIsBlock(block)}
              style={{
                padding:'5px 14px', fontSize:12, borderRadius:6, border:'1px solid', cursor:'pointer',
                borderColor: isBlock===block ? 'var(--accent-primary)' : 'var(--border)',
                background:  isBlock===block ? 'rgba(79,142,247,0.12)' : 'transparent',
                color:       isBlock===block ? 'var(--accent-primary)' : 'var(--text-secondary)',
                fontWeight:  isBlock===block ? 700 : 400,
              }}>
              {block ? 'Block (display)' : 'Inline'}
            </button>
          ))}
        </div>

        {/* Quick examples */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
          {examples.map(ex => (
            <button key={ex.label} onClick={() => setLatex(ex.tex)}
              style={{ padding:'3px 10px', fontSize:11, borderRadius:20, border:'1px solid var(--border)', background:'var(--bg-elevated)', color:'var(--text-secondary)', cursor:'pointer' }}>
              {ex.label}
            </button>
          ))}
        </div>

        {/* LaTeX input */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6, display:'block' }}>LaTeX Expression</label>
          <textarea
            value={latex}
            onChange={e => setLatex(e.target.value)}
            style={{
              width:'100%', minHeight:70, padding:'10px 12px', fontSize:13,
              fontFamily:'var(--font-mono)', background:'var(--bg-elevated)',
              border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)',
              resize:'vertical', boxSizing:'border-box',
            }}
            placeholder="\frac{a}{b}, \sqrt{x}, \int_0^1 f(x)\,dx ..."
            autoFocus
          />
        </div>

        {/* Preview */}
        {preview && (
          <div style={{
            padding:'14px 16px', background:'var(--bg-elevated)', borderRadius:8,
            border:'1px solid var(--border)', marginBottom:16, minHeight:50,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:18, overflowX:'auto',
          }}
            dangerouslySetInnerHTML={{ __html: preview }}
          />
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ padding:'8px 18px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => { if (latex.trim()) { onInsert(latex, isBlock); onClose(); } }}
            disabled={!latex.trim()}
            style={{
              padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer',
              background: latex.trim() ? 'var(--accent-primary)' : 'var(--bg-elevated)',
              color: latex.trim() ? '#fff' : 'var(--text-muted)',
              fontWeight:600, fontSize:13,
            }}>
            Insert Formula →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Image input modal ─────────────────────────────────────────────────────────
function ImageModal({ onInsert, onClose }) {
  const [tab,   setTab]   = useState('url'); // 'url' | 'upload'
  const [url,   setUrl]   = useState('');
  const [alt,   setAlt]   = useState('');
  const [b64,   setB64]   = useState('');
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = r => setB64(r.target.result);
    reader.readAsDataURL(file);
  };

  const doInsert = () => {
    const src = tab === 'url' ? url.trim() : b64;
    if (!src) return;
    onInsert(src, alt);
    onClose();
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:14, border:'1px solid var(--border)', padding:28, maxWidth:480, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🖼 Insert Image</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {[['url','URL'],['upload','Upload']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'5px 16px', fontSize:12, borderRadius:6, cursor:'pointer', border:'1px solid', fontWeight:tab===t?700:400,
                borderColor:tab===t?'var(--accent-primary)':'var(--border)',
                background:tab===t?'rgba(79,142,247,0.12)':'transparent',
                color:tab===t?'var(--accent-primary)':'var(--text-secondary)' }}>
              {l}
            </button>
          ))}
        </div>
        {tab === 'url' ? (
          <div className="form-group">
            <label style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6, display:'block' }}>Image URL</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/image.png"
              style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:13, boxSizing:'border-box' }} />
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:'none' }} />
            <button onClick={() => fileRef.current.click()}
              style={{ padding:'10px 24px', borderRadius:8, border:'2px dashed var(--border)', background:'var(--bg-elevated)', color:'var(--text-secondary)', cursor:'pointer', fontSize:13 }}>
              📁 Choose Image File (max 2MB)
            </button>
            {b64 && <img src={b64} alt="preview" style={{ maxWidth:'100%', maxHeight:150, marginTop:12, borderRadius:8, display:'block', margin:'12px auto 0' }} />}
          </div>
        )}
        <div className="form-group" style={{ marginTop:14 }}>
          <label style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6, display:'block' }}>Alt text (optional)</label>
          <input value={alt} onChange={e => setAlt(e.target.value)} placeholder="Describe the image..."
            style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:13, boxSizing:'border-box' }} />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
          <button onClick={onClose} style={{ padding:'8px 18px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer' }}>Cancel</button>
          <button onClick={doInsert} style={{ padding:'8px 20px', borderRadius:8, border:'none', background:'var(--accent-primary)', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' }}>Insert Image →</button>
        </div>
      </div>
    </div>
  );
}

// ── Main RichEditor component ─────────────────────────────────────────────────
export function RichEditor({ value, onChange, placeholder = 'Start typing...', minHeight = 180 }) {
  const containerRef = useRef(null);
  const quillRef     = useRef(null);
  const valueRef     = useRef(value);

  const [ready,     setReady]     = useState(false);
  const [mathModal, setMathModal] = useState(false);
  const [imgModal,  setImgModal]  = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // Load Quill once
  useEffect(() => {
    loadQuill().then(() => setReady(true)).catch(() => {});
  }, []);

  // Initialise Quill when ready + container mounted
  useEffect(() => {
    if (!ready || !containerRef.current || quillRef.current) return;

    const q = new window.Quill(containerRef.current, {
      theme:       'snow',
      placeholder,
      modules: {
        toolbar: {
          container: TOOLBAR,
          handlers: {
            // Override image + formula buttons — show our custom modals
            image:   () => setImgModal(true),
            formula: () => setMathModal(true),
          },
        },
        history: { delay: 500, maxStack: 200 },
      },
    });

    quillRef.current = q;

    // Force multilingual font — Quill's default CSS overrides to serif/monospace
    // which breaks Devanagari. We must set it explicitly after Quill mounts.
    const editorEl = containerRef.current.querySelector('.ql-editor');
    if (editorEl) {
      editorEl.style.minHeight   = `${minHeight}px`;
      editorEl.style.fontSize    = '15px';
      editorEl.style.lineHeight  = '1.8';
      editorEl.style.fontFamily  = "'Space Grotesk', 'Noto Sans Devanagari', 'Noto Sans', sans-serif";
    }
    // Also set on ql-container so toolbar inherits
    const containerEl = containerRef.current.querySelector('.ql-container');
    if (containerEl) {
      containerEl.style.fontFamily = "'Space Grotesk', 'Noto Sans Devanagari', 'Noto Sans', sans-serif";
    }

    // Load initial content
    if (valueRef.current) {
      q.clipboard.dangerouslyPasteHTML(valueRef.current);
    }

    // Push changes to parent
    q.on('text-change', () => {
      const html = containerRef.current.querySelector('.ql-editor').innerHTML;
      const text = q.getText().trim();
      const out  = text.length === 0 ? '' : html;
      valueRef.current = out;
      onChange?.(out);
      setWordCount(text.length > 0 ? text.trim().split(/\s+/).length : 0);
    });

  }, [ready, placeholder, minHeight, onChange]); // ── end Quill init useEffect

  // ── Sync external value changes (e.g. when parent resets the form) ──────────
  useEffect(() => {
    if (!quillRef.current) return;
    const currentHtml = containerRef.current?.querySelector('.ql-editor')?.innerHTML || '';
    // Only update if value changed externally (avoid cursor jump during typing)
    if (value !== valueRef.current && value !== currentHtml) {
      quillRef.current.clipboard.dangerouslyPasteHTML(value || '');
      valueRef.current = value;
    }
  }, [value]);

  // Insert math formula
  const insertMath = useCallback((latex, isBlock) => {
    const q = quillRef.current;
    if (!q || !window.katex) return;
    const range  = q.getSelection(true);
    const idx    = range ? range.index : q.getLength();

    if (isBlock) {
      // Block formula: insert on its own line
      q.insertText(idx, '\n', 'user');
      const rendered = window.katex.renderToString(latex, { throwOnError:false, displayMode:true });
      q.clipboard.dangerouslyPasteHTML(idx + 1,
        `<p style="text-align:center">${rendered}</p><p><br></p>`
      );
    } else {
      // Inline formula
      const rendered = window.katex.renderToString(latex, { throwOnError:false, displayMode:false });
      q.clipboard.dangerouslyPasteHTML(idx, rendered);
    }
    q.setSelection(q.getLength(), 0);
  }, []);

  // Insert image
  const insertImage = useCallback((src, alt) => {
    const q = quillRef.current;
    if (!q) return;
    const idx = q.getSelection(true)?.index ?? q.getLength();
    q.insertEmbed(idx, 'image', src, 'user');
    q.setSelection(idx + 1, 0);
  }, []);

  if (!ready) return (
    <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
      <div className="spinner" style={{ margin:'0 auto 8px', width:20, height:20 }} />
      Loading editor...
    </div>
  );

  return (
    <>
      {/* Custom editor wrapper — styles match app theme */}
      <div style={{ borderRadius:10, border:'1px solid var(--border)', overflow:'hidden', background:'var(--bg-elevated)' }}>
        <style>{`
          /* Quill toolbar theme override */
          .ql-toolbar.ql-snow {
            border: none !important;
            border-bottom: 1px solid var(--border) !important;
            background: var(--bg-card) !important;
            padding: 8px 10px !important;
            flex-wrap: wrap;
          }
          .ql-toolbar.ql-snow .ql-stroke { stroke: var(--text-secondary) !important; }
          .ql-toolbar.ql-snow .ql-fill   { fill:   var(--text-secondary) !important; }
          .ql-toolbar.ql-snow button:hover .ql-stroke,
          .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: var(--accent-primary) !important; }
          .ql-toolbar.ql-snow button:hover .ql-fill,
          .ql-toolbar.ql-snow button.ql-active .ql-fill   { fill:   var(--accent-primary) !important; }
          .ql-container.ql-snow { border: none !important; }
          .ql-editor { color: var(--text-primary) !important; background: var(--bg-elevated) !important; }
          .ql-editor.ql-blank::before { color: var(--text-muted) !important; }
          .ql-editor pre.ql-syntax {
            background: #1e1e1e !important; color: #d4d4d4 !important;
            border-radius: 8px !important; font-family: 'JetBrains Mono', monospace !important;
            font-size: 13px !important; padding: 14px 16px !important;
          }
          .ql-editor blockquote {
            border-left: 4px solid var(--accent-primary) !important;
            color: var(--text-secondary) !important; padding-left: 16px !important;
          }
          .ql-editor img { max-width: 100%; border-radius: 6px; margin: 8px 0; }
          .ql-picker-label, .ql-picker-item { color: var(--text-secondary) !important; }
          .ql-picker-options {
            background: var(--bg-card) !important;
            border: 1px solid var(--border) !important;
            border-radius: 8px !important;
          }
          .ql-snow .ql-tooltip {
            background: var(--bg-card) !important;
            border: 1px solid var(--border) !important;
            border-radius: 8px !important;
            color: var(--text-primary) !important;
          }
          .ql-snow .ql-tooltip input[type=text] {
            background: var(--bg-elevated) !important;
            border: 1px solid var(--border) !important;
            color: var(--text-primary) !important;
            border-radius: 6px !important;
          }
        `}</style>

        <div ref={containerRef} />

        {/* Word count footer */}
        <div style={{ padding:'4px 12px', borderTop:'1px solid var(--border)', fontSize:11, color:'var(--text-muted)', display:'flex', justifyContent:'space-between' }}>
          <span>Rich editor · Bold Italic Math Code Images supported</span>
          <span>{wordCount} word{wordCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Custom modals */}
      {mathModal && <MathModal onInsert={insertMath} onClose={() => setMathModal(false)} />}
      {imgModal  && <ImageModal onInsert={insertImage} onClose={() => setImgModal(false)} />}
    </>
  );
}

// ── Read-only renderer (used in ExamPage to display questions) ────────────────
// Renders stored HTML safely, applying KaTeX to any math inside
export function RichContent({ html, style }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !html) return;
    ref.current.innerHTML = html;
  }, [html]);

  return (
    <div
      ref={ref}
      style={{
        lineHeight: 1.8,
        fontSize: 15,
        color: 'var(--text-primary)',
        fontFamily: "'Space Grotesk', 'Noto Sans Devanagari', 'Noto Sans', sans-serif",
        ...style,
      }}
      className="rich-content"
    />
  );
}
